import {
  API_TYPES,
  requirementAttributesSchema,
  RequirementWorkflowType,
} from "@clipboard-health/contract-worker-app-bff";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { requestPaginationSchema, responsePaginationSchema } from "@src/appV2/lib/api/types";
import { type UseQueryResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const getRequirementsRequestFilterDtoSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  requirementWorkflowType: z.nativeEnum(RequirementWorkflowType).optional(),
});

export type GetRequirementsRequestFilterDto = z.infer<typeof getRequirementsRequestFilterDtoSchema>;

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getRequirementsRequestDtoSchema = z.object({
  filter: getRequirementsRequestFilterDtoSchema.optional(),
  page: requestPaginationSchema.optional(),
});

export type GetRequirementsRequestDto = z.infer<typeof getRequirementsRequestDtoSchema>;

const getRequirementsResponseItemSchema = z.object({
  id: z.string(),
  type: z.literal(API_TYPES.requirements),
  attributes: requirementAttributesSchema,
});

const getRequirementsResponseSchema = z.object({
  data: z.array(getRequirementsResponseItemSchema),
  links: responsePaginationSchema.optional(),
});

export type GetRequirementsResponse = z.infer<typeof getRequirementsResponseSchema>;
export type GetRequirementsResponseItem = z.infer<typeof getRequirementsResponseItemSchema>;

export function useGetRequirements<TTransformedData = GetRequirementsResponse>(
  queryParams: GetRequirementsRequestDto,
  options: UseGetQueryOptions<GetRequirementsResponse, AxiosError, TTransformedData> = {}
): UseQueryResult<TTransformedData> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/requirements`,
    // this is a paginated endpoint so if needed we can add paginated hook using useInfiniteGetQuery
    queryParams,
    responseSchema: getRequirementsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_REQUIREMENT_FAILURE,
    },
    ...options,
  });
}
